// src/services/stripePortalService.js
import { db } from '../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

/**
 * Redirige l'utilisateur vers le portail client Stripe pour gérer son abonnement
 * @param {string} userId - ID de l'utilisateur Firebase
 * @param {string} userEmail - Email de l'utilisateur
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const redirectToCustomerPortal = async (userId, userEmail) => {
  try {
    console.log('🔍 Début redirectToCustomerPortal');
    console.log('   → userId:', userId);
    console.log('   → userEmail:', userEmail);

    // 1. Récupérer le stripeCustomerId de l'utilisateur
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      console.error('❌ Utilisateur introuvable dans Firestore');
      return { success: false, error: 'Utilisateur introuvable' };
    }
    
    const userData = userSnap.data();
    let stripeCustomerId = userData.stripeCustomerId;
    
    console.log('📊 Données utilisateur:');
    console.log('   → subscriptionType:', userData.subscriptionType);
    console.log('   → stripeCustomerId:', stripeCustomerId || '❌ MANQUANT');
    
    // ✅ FALLBACK : Chercher le customer dans Stripe si manquant
    if (!stripeCustomerId && (userData.subscriptionType === 'silver' || userData.subscriptionType === 'gold')) {
      console.warn('⚠️ stripeCustomerId manquant, tentative de récupération via email...');
      
      try {
        // Appeler une Cloud Function pour chercher le customer par email
        const searchUrl = 'https://us-central1-projectboost-4ad76.cloudfunctions.net/searchStripeCustomer';
        
        const searchResponse = await fetch(searchUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: userEmail })
        });
        
        if (searchResponse.ok) {
          const searchData = await searchResponse.json();
          console.log('📡 Résultat recherche Stripe:', searchData);
          
          if (searchData.success && searchData.customerId) {
            stripeCustomerId = searchData.customerId;
            console.log('✅ Customer trouvé dans Stripe:', stripeCustomerId);
            
            // Sauvegarder dans Firestore pour la prochaine fois
            await updateDoc(userRef, { stripeCustomerId });
            console.log('💾 stripeCustomerId sauvegardé dans Firestore');
          } else {
            console.warn('⚠️ Aucun customer trouvé dans Stripe pour cet email');
          }
        } else {
          console.error('❌ Erreur lors de la recherche du customer:', searchResponse.status);
        }
      } catch (searchError) {
        console.error('❌ Erreur lors de la recherche du customer:', searchError);
        // On continue sans bloquer
      }
    }
    
    // Vérification finale
    if (!stripeCustomerId) {
      console.error('❌ Impossible de récupérer le stripeCustomerId');
      return { 
        success: false, 
        error: 'Aucun ID client Stripe trouvé. Veuillez contacter le support avec votre email : ' + userEmail 
      };
    }
    
    console.log('✅ stripeCustomerId disponible:', stripeCustomerId);
    
    // 2. Appeler la Cloud Function pour créer une session du portail client
    const createPortalSessionUrl = 'https://us-central1-projectboost-4ad76.cloudfunctions.net/createStripePortalSession';
    
    console.log('📡 Appel de createStripePortalSession...');
    const response = await fetch(createPortalSessionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customerId: stripeCustomerId,
        returnUrl: window.location.origin + '/pricing'
      })
    });
    
    console.log('📡 Réponse reçue, status:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ Erreur de la Cloud Function:', errorData);
      return { success: false, error: errorData.error || 'Erreur lors de la création de la session' };
    }
    
    const data = await response.json();
    console.log('📊 Données reçues:', data);
    
    // 3. Rediriger vers le portail Stripe
    if (data.url) {
      console.log('🚀 Redirection vers le portail Stripe:', data.url);
      window.location.href = data.url;
      return { success: true };
    } else {
      console.error('❌ URL du portail non reçue dans la réponse');
      return { success: false, error: 'URL du portail non reçue' };
    }
    
  } catch (error) {
    console.error('❌ Erreur lors de la redirection vers le portail:', error);
    console.error('   → Message:', error.message);
    console.error('   → Stack:', error.stack);
    return { success: false, error: error.message };
  }
};

/**
 * Vérifie si l'utilisateur a un abonnement actif gérable
 * @param {Object} userData - Données de l'utilisateur
 * @returns {boolean}
 */
export const hasManageableSubscription = (userData) => {
  if (!userData) {
    console.log('⚠️ hasManageableSubscription: userData est null/undefined');
    return false;
  }
  
  const subscriptionType = userData.subscriptionType?.toLowerCase();
  const isManageable = subscriptionType === 'silver' || subscriptionType === 'gold';
  
  console.log('🔍 hasManageableSubscription:');
  console.log('   → subscriptionType:', subscriptionType);
  console.log('   → isManageable:', isManageable);
  
  return isManageable;
};

/**
 * Fonction helper pour débugger les problèmes de portail client
 * @param {string} userId - ID de l'utilisateur
 * @param {string} userEmail - Email de l'utilisateur
 */
export const debugPortalAccess = async (userId, userEmail) => {
  console.log('\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('🔍 DEBUG - ACCÈS PORTAIL CLIENT');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      console.error('❌ Utilisateur introuvable dans Firestore');
      return;
    }
    
    const userData = userSnap.data();
    
    console.log('\n👤 Données utilisateur:');
    console.log('   • userId:', userId);
    console.log('   • userEmail:', userEmail);
    console.log('   • subscriptionType:', userData.subscriptionType || '❌ MANQUANT');
    console.log('   • subscriptionStatus:', userData.subscriptionStatus || '❌ MANQUANT');
    console.log('   • stripeCustomerId:', userData.stripeCustomerId || '❌ MANQUANT');
    console.log('   • stripeSubscriptionId:', userData.stripeSubscriptionId || '❌ MANQUANT');
    console.log('   • diagnosticsLimit:', userData.diagnosticsLimit);
    
    console.log('\n✅ Vérifications:');
    console.log('   • A un abonnement Silver/Gold:', hasManageableSubscription(userData) ? '✅ OUI' : '❌ NON');
    console.log('   • stripeCustomerId présent:', userData.stripeCustomerId ? '✅ OUI' : '❌ NON');
    
    if (!userData.stripeCustomerId) {
      console.log('\n⚠️ PROBLÈME DÉTECTÉ:');
      console.log('   Le stripeCustomerId est manquant.');
      console.log('   Causes possibles:');
      console.log('   1. Le webhook Stripe n\'a pas été déclenché');
      console.log('   2. Le webhook a échoué');
      console.log('   3. L\'abonnement a été créé avant l\'implémentation du webhook');
      console.log('\n💡 Solutions:');
      console.log('   1. Vérifier les logs du webhook dans Firebase Functions');
      console.log('   2. Tester la fonction searchStripeCustomer');
      console.log('   3. Contacter le support avec l\'email:', userEmail);
    }
    
    console.log('\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
    
  } catch (error) {
    console.error('❌ Erreur dans debugPortalAccess:', error);
  }
};